import fs from "node:fs";
import path from "node:path";
import type { CommonAction, Page, ResponseData } from "@iflyrpa/share";
import domSerializer from "dom-serializer";
import { type ChildNode, DomHandler, type Element } from "domhandler";
import { DomUtils, Parser } from "htmlparser2";
import mime from "mime-types";

import { Http } from "./http";

/**
 * 生成两数之间的随机整数
 * @param min
 * @param max
 * @returns
 */
export function getRandomInRange(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const getFileState = (filePath: string) => {
    // 获取文件名
    const filename = path.basename(filePath);
    // 获取文件类型
    const fileType = mime.lookup(filePath);
    const stats = fs.statSync(filePath);
    // 获取文件大小
    const fileSize = stats.size;
    // 获取最后修改时间
    const lastModifiedDate = stats.mtime;

    return {
        name: filename,
        type: fileType,
        size: fileSize,
        lastModifiedDate: lastModifiedDate.toString(),
    };
};

export type AspectRatio = [number, number];
export type CropCoordinates = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
};

/**
 * 获取特定的比例切割图片，获取切割的坐标
 * @param width 图片长度
 * @param height 图片高度
 * @param aspectRatio 切割比例：[宽度，高度]
 * @returns
 */
export const calculateCropCoordinates = (width: number, height: number, aspectRatio: AspectRatio): CropCoordinates => {
    const targetRatio = aspectRatio[0] / aspectRatio[1];
    let cropWidth: number;
    let cropHeight: number;

    if (width / height > targetRatio) {
        // 图片过宽，需要裁切宽度
        cropHeight = height;
        cropWidth = height * targetRatio;
    } else {
        // 图片过高，需要裁切高度
        cropWidth = width;
        cropHeight = width / targetRatio;
    }

    const x1 = (width - cropWidth) / 2;
    const y1 = (height - cropHeight) / 2;
    const x2 = x1 + cropWidth;
    const y2 = y1 + cropHeight;

    // 返回百分比坐标
    return {
        x1: x1 / width,
        y1: y1 / height,
        x2: x2 / width,
        y2: y2 / height,
    };
};

// 提取 html 中的 img 标签
export const extractImgTag = (html: string) => {
    const srcList: string[] = [];

    const parser = new Parser({
        onopentag(name, attributes) {
            const src = attributes.src?.trim();
            const currentSrc = attributes.currentSrc?.trim();
            if (name === "img" && (src || currentSrc)) {
                srcList.push(src || currentSrc);
            }
        },
    });

    parser.write(html);
    parser.end();

    return srcList;
};

/**
 * 格式化 HTML 内容，删除无效的 section（没有 img 或 img src 为空）
 * @param html
 */
export const formatSectionHtml = (html: string): string => {
    const handler = new DomHandler();
    const parser = new Parser(handler, {
        decodeEntities: false,
    });

    parser.write(html);
    parser.end();

    const dom = handler.dom;

    // 找出所有 section
    const sections = DomUtils.findAll((node) => node.type === "tag" && node.name === "section", dom) as Element[];

    sections.forEach((section) => {
        // 找出 section 内的 img
        const imgs = DomUtils.findAll(
            (node) => node.type === "tag" && node.name === "img",
            section.children || [],
        ) as Element[];

        // 没有 img，不处理（避免误删）
        if (imgs.length === 0) return;

        const hasValidImg = imgs.some((img) => {
            const src = img.attribs?.src?.trim();
            return Boolean(src);
        });

        // section 内不存在任何有效 img，删除整个 section
        if (!hasValidImg) {
            DomUtils.removeElement(section);
        }
    });

    return domSerializer(dom, { encodeEntities: false });
};
/**
 * 时间格式化
 * @param time
 * @param format 默认 yyyy-MM-dd HH:mm
 * @returns 时间字符串
 */
export namespace TimeFormatter {
    export function format(timestamp: number, formatStr = "yyyy-MM-dd hh:mm"): string {
        // 自动判断：如果是10位（秒），就转为毫秒
        if (timestamp.toString().length === 10) {
            timestamp *= 1000;
        }

        const date = new Date(timestamp);

        const map: Record<string, string> = {
            yyyy: String(date.getFullYear()),
            yy: String(date.getFullYear()).slice(-2),
            MM: String(date.getMonth() + 1).padStart(2, "0"),
            dd: String(date.getDate()).padStart(2, "0"),
            hh: String(date.getHours()).padStart(2, "0"),
            mm: String(date.getMinutes()).padStart(2, "0"),
            ss: String(date.getSeconds()).padStart(2, "0"),
            M: String(date.getMonth() + 1), // 月不带前导零
            d: String(date.getDate()), // 日不带前导零
            h: String(date.getHours()), // 不带前导零
            m: String(date.getMinutes()), // 不带前导零
            s: String(date.getSeconds()), // 不带前导零
        };

        return formatStr.replace(/yyyy|yy|MM|dd|hh|mm|ss|M|d|h|m|s/g, (key) => map[key]);
    }
}

export const replaceImgSrc = (html: string, callback: (dom: Element) => Element) => {
    let lastedHtml = html;

    const isImageTag = (node: ChildNode): node is Element => node.type === "tag" && node.name === "img";

    const traverseDom = (node: ChildNode) => {
        if (DomUtils.hasChildren(node)) {
            for (const child of node.children) {
                if (isImageTag(child)) {
                    callback(child);
                }
                traverseDom(child);
            }
        }
    };

    const handler = new DomHandler((error, dom) => {
        if (error) {
            console.log(error);
        } else {
            // 处理 DOM 节点的函数
            dom.forEach(traverseDom);
        }

        lastedHtml = domSerializer(dom, { decodeEntities: false });
    });

    const parser = new Parser(handler, { decodeEntities: false });
    parser.write(html);
    parser.end();

    return lastedHtml;
};

export const stringifyError = (error: unknown) => {
    if (error instanceof Error) {
        return error.toString();
    }

    if (typeof error === "object") {
        return JSON.stringify(error);
    }

    return error;
};

/**
 * 创建一个函数，该函数会依次执行传入的函数列表，直到有一个函数成功或所有函数都失败。
 *
 * @template P - 函数的参数类型，为一个任意长度的数组。
 * @template T - 函数的返回类型，为一个 Promise 的解析值类型。
 * @param {CommonAction<P, T>[]} actions - 一个函数列表，每个函数的类型为 `(...p: P) => Promise<T>`。
 * @returns {CommonAction<P, T>} - 返回一个新的函数，其类型与传入的函数列表中的函数类型相同。
 * 当调用该函数时，会依次执行 `actions` 中的函数，直到有一个函数成功（即返回一个解析的 Promise），
 * 或者所有函数都失败（即抛出异常）。如果所有函数都失败，则抛出最后一个错误。
 *
 * @example
 * const action1 = async (num) => {
 *   if (num > 0) return "Action 1 succeeded";
 *   throw new Error("Action 1 failed");
 * };
 *
 * const action2 = async (num) => {
 *   if (num > 1) return "Action 2 succeeded";
 *   throw new Error("Action 2 failed");
 * };
 *
 * const combinedAction = executeAction(action1, action2);
 *
 * (async () => {
 *   try {
 *     const result = await combinedAction(1);
 *     console.log(result); // 输出: "Action 2 succeeded"
 *   } catch (error) {
 *     console.error(error.message);
 *   }
 * })();
 */
export const executeAction = <P, T>(...actions: CommonAction<P, T>[]): CommonAction<P, T> => {
    return async (task, params) => {
        let result: ResponseData<T> = {
            code: 500,
            message: "",
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            data: null as any,
        };

        for (let index = 0; index < actions.length; index++) {
            const action = actions[index];

            try {
                result = await action(task, params);
            } catch (error) {
                result = Http.handleApiError(error);
            }

            // code === 0 表示成功, code === 414 虽然表示失败，但是不需要继续执行后续的 action
            if (result.code === 0 || result.code === 414) {
                return result;
            }

            // 不是最后一个 action，上报错误信息
            if (index !== actions.length - 1) {
                task.logger.error(result.message, stringifyError(result.data), result.extra);
            }
        }

        // 所有 action 都执行完毕但都失败了，返回最后一个失败结果
        return result;
    };
};

/**
 * 将 HTML 内容复制到剪贴板
 * @param page - 页面对象
 * @param html - HTML 内容
 * @param options - 选项，包括格式和回退
 * @returns 无返回值
 */
export async function copyHtmlToClipboard(page: Page, html: string, options = { format: "text/html", fallback: true }) {
    try {
        // 尝试使用原生 API
        await page.evaluate(
            async ({ htmlContent, format }) => {
                const blob = new Blob([htmlContent], { type: format });
                await navigator.clipboard.write([new ClipboardItem({ [format]: blob })]);
            },
            { htmlContent: html, format: options.format },
        );
    } catch (error) {
        if (options.fallback) {
            // 回退到 DOM 操作
            await page.evaluate((htmlContent) => {
                const div = document.createElement("div");
                div.contentEditable = "true";
                div.style.cssText = `
          position: fixed;
          left: -9999px;
          opacity: 0;
          pointer-events: none;
        `;

                div.innerHTML = htmlContent;
                document.body.appendChild(div);

                const range = document.createRange();
                range.selectNodeContents(div);

                // biome-ignore lint/style/noNonNullAssertion: <忽略错误>
                const selection = window.getSelection()!;
                selection.removeAllRanges();
                selection.addRange(range);

                document.execCommand("copy");
                document.body.removeChild(div);
                selection.removeAllRanges();
            }, html);
        }
    }
}

/**
 * 通用弹窗监听处理器
 * @param page - Playwright 页面对象
 * @param popupSelector - 弹窗的选择器（字符串或 Locator）
 * @param popKey - 弹窗中的唯一键（字符串）
 * @param buttonSelector - 弹窗中需要点击的按钮选择器
 * @param options - 可选配置项
 */
export function PopupListener(
    page: Page,
    popupSelector: string,
    popKey: string,
    buttonSelector: string,
    options: {
        checkInterval?: number;
        stopAfterClick?: boolean;
    } = {},
) {
    const { checkInterval = 1000, stopAfterClick = false } = options;

    const popup = page.locator(popupSelector);
    const button = page.locator(buttonSelector);

    const intervalId = setInterval(async () => {
        try {
            if (
                (await popup.count()) > 0 &&
                (await popup.isVisible()) &&
                ((await popup.textContent()) || "").includes(popKey)
            ) {
                await button.click({ force: true });

                if (stopAfterClick) {
                    clearInterval(intervalId);
                }
            }
        } catch (err) {}
    }, checkInterval);

    return () => {
        clearInterval(intervalId);
    };
}

// 将 url 的 queryString 解析成对象
export const parseUrlQueryString = (url: string) => {
    const queryStringObject: Record<string, string> = {};

    // 检查 URL 是否包含查询参数
    if (!url || !url.includes("?")) {
        return queryStringObject;
    }

    // 提取查询字符串部分
    const queryString = url.split("?")[1];

    // 检查查询字符串是否为空
    if (!queryString) {
        return queryStringObject;
    }

    // 处理 hash 部分
    const queryWithoutHash = queryString.split("#")[0];

    // 分割查询参数
    const queryStringArray = queryWithoutHash.split("&");

    for (const item of queryStringArray) {
        // 跳过空参数
        if (!item) continue;

        const equalIndex = item.indexOf("=");

        // 处理没有值的参数 (如 ?param)
        if (equalIndex === -1) {
            queryStringObject[item] = "";
        } else {
            const key = item.slice(0, equalIndex);
            const value = item.slice(equalIndex + 1);

            // 尝试解码，如果解码失败则使用原始值
            try {
                queryStringObject[key] = decodeURIComponent(value);
            } catch (e) {
                queryStringObject[key] = value;
            }
        }
    }

    return queryStringObject;
};

/**
 * 合并默认参数
 * @param params
 * @param defaults
 * @returns
 */
export const defaultParams = <P = Record<string, unknown>>(params: P, defaults: Partial<P>): P => {
    return Object.assign({}, defaults, params);
};
